export interface MicroQuizOptionInput {
  text: string;
  correct: boolean;
  explanation?: string;
}

export interface MicroQuizQuestionInput {
  question: string;
  explanation?: string;
  options: MicroQuizOptionInput[];
}

export interface MicroQuizData {
  title?: string;
  max_questions?: number;
  max_answers?: number;
  questions: MicroQuizQuestionInput[];
}

export interface MicroQuizOption extends MicroQuizOptionInput {
  id: string;
}

export interface MicroQuizQuestion {
  id: string;
  question: string;
  explanation?: string;
  options: MicroQuizOption[];
}

function isDefined<T>(value: T | undefined | null): value is T {
  return value != null;
}

function compact<T>(items: (T | undefined | null)[]): T[] {
  return items.flatMap((item) => (item != null ? [item] : []));
}

export interface MicroQuizResult {
  correctAnswers: number;
  totalQuestions: number;
  finishedAt: string;
}

export interface ExtractedMicroQuiz {
  before: string;
  after: string;
  quiz?: MicroQuizData;
}

const QUIZ_BLOCK_REGEX = /```microquiz\s*([\s\S]*?)```/i;

function parseScalar(value: string): string | number | boolean {
  const trimmed = value.trim();
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  return trimmed.replace(/^['"]|['"]$/g, '');
}

function parseYamlQuiz(rawYaml: string): unknown {
  const lines = rawYaml
    .split('\n')
    .map((line) => line.replace(/\t/g, '  '))
    .filter((line) => line.trim() && !line.trim().startsWith('#'));

  const result: Record<string, unknown> = {};
  const questions: Array<Record<string, unknown>> = [];
  let currentQuestion: Record<string, unknown> | null = null;
  let currentOptions: Array<Record<string, unknown>> | null = null;

  for (const rawLine of lines) {
    const indent = rawLine.match(/^\s*/)?.[0].length ?? 0;
    const line = rawLine.trim();

    if (line === 'questions:') {
      result.questions = questions;
      currentQuestion = null;
      currentOptions = null;
      continue;
    }

    if (line.startsWith('- ') && indent === 0) {
      continue;
    }

    if (line.startsWith('- ') && indent === 2) {
      const question: Record<string, unknown> = {};
      questions.push(question);
      currentQuestion = question;
      currentOptions = null;

      const inline = line.slice(2);
      if (inline.includes(':')) {
        const colon = inline.indexOf(':');
        const key = inline.slice(0, colon).trim();
        const value = inline.slice(colon + 1);
        question[key] = parseScalar(value);
      }
      continue;
    }

    if (!currentQuestion && indent === 0 && line.includes(':')) {
      const colon = line.indexOf(':');
      const key = line.slice(0, colon).trim();
      const value = line.slice(colon + 1);
      result[key] = parseScalar(value);
      continue;
    }

    if (!currentQuestion) continue;

    if (indent === 4 && line === 'options:') {
      currentOptions = [];
      currentQuestion.options = currentOptions;
      continue;
    }

    if (indent === 4 && line.includes(':')) {
      const colon = line.indexOf(':');
      const key = line.slice(0, colon).trim();
      const value = line.slice(colon + 1);
      currentQuestion[key] = parseScalar(value);
      continue;
    }

    if (indent === 6 && line.startsWith('- ') && currentOptions) {
      const option: Record<string, unknown> = {};
      currentOptions.push(option);

      const inline = line.slice(2);
      if (inline.includes(':')) {
        const colon = inline.indexOf(':');
        const key = inline.slice(0, colon).trim();
        const value = inline.slice(colon + 1);
        option[key] = parseScalar(value);
      }
      continue;
    }

    if (indent === 8 && line.includes(':') && currentOptions && currentOptions.length > 0) {
      const option = currentOptions[currentOptions.length - 1];
      const colon = line.indexOf(':');
      const key = line.slice(0, colon).trim();
      const value = line.slice(colon + 1);
      option[key] = parseScalar(value);
    }
  }

  return result;
}

function toPositiveInt(value: unknown, fallback: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(1, Math.floor(parsed));
}

function sanitizeOption(raw: unknown): MicroQuizOptionInput | undefined {
  if (!raw || typeof raw !== 'object') return undefined;

  const r = raw as Partial<MicroQuizOptionInput>;
  const text = String(r.text ?? '').trim();

  if (!text || typeof r.correct !== 'boolean') return undefined;

  const result: MicroQuizOptionInput = { text, correct: r.correct };

  if (typeof r.explanation === 'string' && r.explanation.trim()) {
    result.explanation = r.explanation.trim();
  }

  return result;
}

function sanitizeQuestion(raw: unknown): MicroQuizQuestionInput | undefined {
  if (!raw || typeof raw !== 'object') return undefined;

  const r = raw as Partial<MicroQuizQuestionInput>;
  const question = String(r.question ?? '').trim();

  if (!question || !Array.isArray(r.options)) return undefined;

  const options = compact(r.options.map(sanitizeOption));

  if (options.length < 2 || !options.some((o) => o.correct)) return undefined;

  const result: MicroQuizQuestionInput = { question, options };

  if (typeof r.explanation === 'string' && r.explanation.trim()) {
    result.explanation = r.explanation.trim();
  }

  return result;
}

function sanitizeQuiz(rawQuiz: unknown): MicroQuizData | undefined {
  if (!rawQuiz || typeof rawQuiz !== 'object') return undefined;

  const raw = rawQuiz as Partial<MicroQuizData>;
  if (!Array.isArray(raw.questions)) return undefined;

  const questions = compact(raw.questions.map(sanitizeQuestion));

  if (questions.length === 0) return undefined;

  const result: MicroQuizData = { questions };

  if (typeof raw.title === 'string' && raw.title.trim()) {
    result.title = raw.title.trim();
  }

  result.max_questions = toPositiveInt(raw.max_questions, 3);
  result.max_answers = toPositiveInt(raw.max_answers, 3);

  return result;
}

/**
 * Parses a raw YAML/JSON string from a ```microquiz block and returns a
 * validated MicroQuizData object, or undefined if parsing fails.
 * Exported so that the unified content-segment extractor can reuse it.
 */
export function parseQuizFromString(rawPayload: string): MicroQuizData | undefined {
  if (!rawPayload.trim()) return undefined;
  try {
    const parsed = JSON.parse(rawPayload);
    return sanitizeQuiz(parsed);
  } catch {
    const yamlParsed = parseYamlQuiz(rawPayload);
    return sanitizeQuiz(yamlParsed);
  }
}

export function extractMicroQuiz(markdown: string): ExtractedMicroQuiz {
  const match = markdown.match(QUIZ_BLOCK_REGEX);

  if (!match || typeof match.index !== 'number') {
    return { before: markdown, after: '' };
  }

  const blockStart = match.index;
  const blockEnd = blockStart + match[0].length;
  const before = markdown.slice(0, blockStart).trimEnd();
  const after = markdown.slice(blockEnd).trimStart();
  const rawPayload = match[1].trim();

  if (!rawPayload) {
    return { before, after };
  }

  try {
    const parsed = JSON.parse(rawPayload);
    const quiz = sanitizeQuiz(parsed);

    if (!quiz) {
      const yamlParsed = parseYamlQuiz(rawPayload);
      const yamlQuiz = sanitizeQuiz(yamlParsed);
      if (!yamlQuiz) return { before, after };
      return { before, after, quiz: yamlQuiz };
    }

    return { before, after, quiz };
  } catch {
    const yamlParsed = parseYamlQuiz(rawPayload);
    const yamlQuiz = sanitizeQuiz(yamlParsed);
    if (!yamlQuiz) return { before, after };
    return { before, after, quiz: yamlQuiz };
  }
}

export function shuffleArray<T>(items: T[]): T[] {
  const clone = [...items];

  for (let index = clone.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [clone[index], clone[randomIndex]] = [clone[randomIndex], clone[index]];
  }

  return clone;
}

export function pickRandom<T>(items: T[], count: number): T[] {
  if (count >= items.length) return shuffleArray(items);
  return shuffleArray(items).slice(0, Math.max(0, count));
}

export function buildQuizAttempt(
  quiz: MicroQuizData,
  maxQuestions: number,
  maxAnswers: number
): MicroQuizQuestion[] {
  const questionLimit = Math.max(1, Math.min(maxQuestions, quiz.questions.length));

  return pickRandom(quiz.questions, questionLimit).flatMap(
    (question, questionIndex): MicroQuizQuestion[] => {
      const correctOptions = question.options.filter((option) => option.correct);
      const incorrectOptions = question.options.filter((option) => !option.correct);

      if (correctOptions.length === 0) return [];

      const requiredAnswers = Math.max(2, Math.min(maxAnswers, question.options.length));
      const selectedCorrect = pickRandom(correctOptions, 1);
      const selectedIncorrect = pickRandom(incorrectOptions, Math.max(1, requiredAnswers - 1));

      const finalOptions = shuffleArray([...selectedCorrect, ...selectedIncorrect]);

      if (finalOptions.length < 2 || !finalOptions.some((option) => option.correct)) {
        return [];
      }

      const built: MicroQuizQuestion = {
        id: `q-${questionIndex}-${question.question.slice(0, 24)}`,
        question: question.question,
        options: finalOptions.map((option, optionIndex) => ({
          ...option,
          id: `o-${questionIndex}-${optionIndex}`,
        })),
      };

      if (question.explanation !== undefined) {
        built.explanation = question.explanation;
      }

      return [built];
    }
  );
}

export function isValidStoredResult(value: unknown): value is MicroQuizResult {
  if (!value || typeof value !== 'object') return false;
  const result = value as Partial<MicroQuizResult>;

  return (
    Number.isFinite(result.correctAnswers) &&
    Number.isFinite(result.totalQuestions) &&
    typeof result.finishedAt === 'string' &&
    result.totalQuestions! > 0
  );
}

